import { queueMacrotask } from 'module/helpers/macrotask';
import { useTranslate } from 'module/hooks/use-translation';
import {
  CustomLayoutDrawerContent,
  DrawerBreadcrumb,
  DrawerBreadCrumbs,
} from 'module/ui/components/atoms/drawer/custom-layout-drawer';
import { Form } from 'module/ui/components/atoms/form';
import { Input } from 'module/ui/components/atoms/input/input';
import { Button } from '@rocketlane/ui';
import { InputRef } from 'antd';
import { WebsiteContent } from 'api/knowledge-base/types';
import { useGetContentFromWebsiteUrlQuery } from 'queries/knowledge-base';
import { useState, useRef, useEffect, FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FoldersChooser } from './folders-chooser';

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  .ant-form {
    flex: 1;
  }
`;
const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

interface FormValues {
  websiteUrl: string;
}

export type AddWebsitePayload = {
  apiDocVersion: boolean;
  websiteUrl: string;
  selectedFolderPaths: WebsiteContent[];
};

interface Props {
  apiDocVersion: boolean;
  onClose: () => void;
  onDone: (payload: AddWebsitePayload) => Promise<void>;
  breadcrumbs?: DrawerBreadcrumb[];
}

export const AddWebsite: FC<Props> = ({
  apiDocVersion,
  onClose,
  onDone,
  breadcrumbs,
}) => {
  const [form] = Form.useForm<FormValues>();
  const [actionLoading, setActionLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const translate = useTranslate();
  const [selectedWebsiteUrl, setSelectedWebsiteUrl] = useState('');
  const [selectedFolderPaths, setSelectedFolderPaths] = useState<
    WebsiteContent[]
  >([]);
  const handleSelectionChange = useCallback(
    (selectedItemInfos: WebsiteContent[]) => {
      setSelectedFolderPaths(selectedItemInfos);
    },
    [],
  );
  const { data: websiteContent, isLoading: isQueryLoading } =
    useGetContentFromWebsiteUrlQuery(selectedWebsiteUrl, {
      enabled: !!selectedWebsiteUrl,
    });
  const isWebsiteContentLoading = !!selectedWebsiteUrl && isQueryLoading;

  useEffect(() => {
    form.resetFields();
    setIsFormValid(false);
    setActionLoading(false);
    inputRef.current?.focus();
  }, [form]);

  const onFormValuesChange = useCallback(() => {
    queueMacrotask(() => {
      form
        .validateFields()
        .then(({ websiteUrl }) => {
          if (websiteUrl) {
            setIsFormValid(true);
          } else {
            setIsFormValid(false);
          }
        })
        .catch(() => setIsFormValid(false));
    });
  }, [form]);

  const handleNext = useCallback(() => {
    setActionLoading(true);
    onDone({
      apiDocVersion,
      websiteUrl: selectedWebsiteUrl,
      selectedFolderPaths,
    }).finally(() => {
      setActionLoading(false);
    });
  }, [onDone, apiDocVersion, selectedWebsiteUrl, selectedFolderPaths]);
  const handleAddWebsite = useCallback(() => {
    form
      .validateFields()
      .then((values) => {
        setSelectedWebsiteUrl(values.websiteUrl);
      })
      .catch(() => {});
  }, [form]);

  return (
    <CustomLayoutDrawerContent>
      <CustomLayoutDrawerContent.Title
        breadcrumbs={
          breadcrumbs ? (
            <DrawerBreadCrumbs breadcrumbs={breadcrumbs} />
          ) : undefined
        }
      >
        {apiDocVersion ? (
          <FormattedMessage id="knowledge_base.add_api_doc.title" />
        ) : (
          <FormattedMessage id="knowledge_base.add_website.title" />
        )}
      </CustomLayoutDrawerContent.Title>
      <CustomLayoutDrawerContent.Content>
        <ContentWrapper>
          <InputWrapper>
            <Form
              form={form}
              layout="vertical"
              onValuesChange={onFormValuesChange}
            >
              <Form.Item
                name="websiteUrl"
                label={
                  <FormattedMessage id="knowledge_base.add_website.url.label" />
                }
                rules={[
                  {
                    required: true,
                    validator: (_, value) => {
                      if (selectedWebsiteUrl && !value) {
                        return Promise.resolve();
                      }
                      if (!value) {
                        return Promise.reject(
                          new Error(translate('input.required')),
                        );
                      }
                      const regex =
                        /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;

                      if (!regex.test(value)) {
                        return Promise.reject(
                          new Error(
                            translate('knowledge_base.add_website.url.invalid'),
                          ),
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
                requiredMark={'optional'}
              >
                <Input
                  ref={inputRef}
                  placeholder={translate(
                    'knowledge_base.add_website.url.placeholder',
                  )}
                  size="middle"
                  autoFocus
                  disabled={actionLoading}
                />
              </Form.Item>
            </Form>
            <Button
              variant="primary"
              onClick={handleAddWebsite}
              disabled={!isFormValid}
              loading={isWebsiteContentLoading}
            >
              <FormattedMessage id="knowledge_base.add_website.actions.add" />
            </Button>
          </InputWrapper>
          {selectedWebsiteUrl && !isWebsiteContentLoading && (
            <FoldersChooser
              onSelectionChange={handleSelectionChange}
              data={websiteContent?.data ?? []}
              apiDocVersion={apiDocVersion}
            />
          )}
        </ContentWrapper>
      </CustomLayoutDrawerContent.Content>
      <CustomLayoutDrawerContent.Footer $rightAlignedAction>
        <Button variant="secondary" onClick={onClose} disabled={actionLoading}>
          <FormattedMessage id="cancel" />
        </Button>
        <Button
          variant="primary"
          onClick={handleNext}
          disabled={selectedFolderPaths.length === 0 || actionLoading}
          loading={actionLoading}
        >
          <FormattedMessage id="knowledge_base.drawer.actions.add_to_knowledge_base" />
        </Button>
      </CustomLayoutDrawerContent.Footer>
    </CustomLayoutDrawerContent>
  );
};
